import SimpleLayout from 'layouts/SimpleLayout.vue';
import Activities from 'pages/Activities.vue';
import Clients from 'pages/Clients.vue';
import Encryption from 'pages/Encryption.vue';
import Home from 'pages/Home.vue';
import Login from 'pages/Login.vue';
import PendingSignatures from 'pages/PendingSignatures.vue';
import Programs from 'pages/Programs.vue';
import CollectData from 'pages/Run/CollectData.vue';
import Done from 'pages/Run/Done.vue';
import Overview from 'pages/Run/Overview.vue';
import PrepareGroupSession from 'pages/Run/PrepareGroupSession.vue';
import NoteTemplates from 'pages/Settings/NoteTemplates.vue';
import OfflineAccess from 'pages/Settings/OfflineAccess.vue';
import { AUDITOR_PRIVILEGES } from 'src/domains/noteAuditor/domain';

import { userRoles } from '../utils/AppUtils';

const routes = [
  {
    path: '/u',
    component: SimpleLayout,
    children: [
      {
        path: '/region',
        name: 'region',
        component: () => import('pages/Region.vue'),
        meta: {
          public: true,
        },
      },
      {
        path: '/login',
        name: 'login',
        component: Login,
        meta: {
          public: true,
          offlineModePage: true,
        },
      },
      {
        path: '/signup',
        name: 'signup',
        component: () => import('pages/Signup/Signup.vue'),
        meta: {
          public: true,
          requiresRegion: false,
        },
      },
      {
        path: '/signup-clinic',
        name: 'Sign up new clinic',
        component: () => import('pages/Signup/SignupClinic.vue'),
        meta: {
          public: true,
          requiresRegion: true,
        },
      },
      {
        path: '/signup/install/:categoryTag/:packageUrlShortCode',
        name: 'Signup with package',
        component: () => import('pages/Signup/SignupClinic.vue'),
        meta: {
          public: true,
          requiresRegion: true,
        },
      },
      {
        path: '/signup/verify-email',
        name: 'Verify e-mail',
        component: () => import('pages/Signup/VerifyEmail.vue'),
        meta: {
          acceptUnverified: true,
          allowNoBilling: true,
          allowNoClientsOrClinic: true,
        },
      },
      {
        path: '/signup/enable-mfa',
        name: 'Enable MFA',
        component: () => import('pages/Signup/EnableMFA.vue'),
        meta: {
          acceptNoMFA: true,
          allowNoBilling: true,
          allowNoClientsOrClinic: true,
        },
      },
      {
        path: '/signup/terms',
        name: 'Terms',
        component: () => import('pages/Signup/Terms.vue'),
        meta: {
          public: false,
          allowNoBilling: true,
          allowNoClientsOrClinic: false,
        },
      },
      {
        path: '/signup/get-started',
        name: 'Get started',
        component: () => import('pages/Signup/GetStarted.vue'),
      },
      {
        path: '/email-action-handler',
        name: 'Email Action Handler',
        component: () => import('pages/EmailActionHandler.vue'),
        meta: {
          public: true,
          preventRedirect: true,
          requiresRegion: true,
        },
      },
      {
        path: '/encryption',
        name: 'Encryption',
        component: Encryption,
        meta: {
          public: true,
          offlineModePage: true,
        },
      },
      {
        path: '/run/:sessionId/collect',
        name: 'session-data-collection',
        component: CollectData,
        meta: {
          offlineModePage: true,
          requiresClient: true,
          requiresActiveSubscription: true,
          requiresClientLicense: true,
        },
      },
      {
        path: '/run/:sessionId/supervise',
        name: 'supervise-session',
        component: () => import('pages/Run/SuperviseSession.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          requiresClientLicense: true,
        },
      },
      {
        path: '/run/:sessionId/supervise/modify-program/:programId',
        component: () => import('pages/EditProgram.vue'),
        name: 'supervise-modify-program',
        props: (route) => ({
          baseUrl: `/run/${route.params.sessionId}/supervise`,
          showPostSaveDialog: true,
        }),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          requiresClientLicense: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
        },
      },
      // The design system kitchen sink page is only included in non-production builds, as it is used for testing
      // and development purposes and should not be accessible in the production environment.
      ...(process.env.ENV_TYPE !== 'production' ? [{
        path: '/design-system/:id?',
        name: 'Design System Kitchen Sink',
        component: () => import('src/design-system/kitchen-sink/KitchenSink.vue'),
      }, {
        // The bounded-context catalogue, on the same terms as the kitchen sink above: an internal
        // reference for the DDD model, generated by `npm run ddd:docs`.
        path: '/contexts/:id?',
        name: 'DDD Catalog',
        component: () => import('src/tools/ddd-catalog/DddCatalog.vue'),
      }] : []),
    ],
  },
  {
    path: '/s',
    component: () => import('layouts/AnonymousLayout.vue'),
    children: [
      {
        path: '/store',
        name: 'Store',
        component: () => import('pages/Store/Store.vue'),
        props: { baseUrl: '/store' },
        meta: {
          public: true,
          preventRedirect: true,
        },
      },
      {
        path: '/store/:categoryTag',
        name: 'Store - category',
        component: () => import('pages/Store/Store.vue'),
        props: { baseUrl: '/store' },
        meta: {
          public: true,
          preventRedirect: true,
        },
      },
      {
        path: '/store/:categoryTag/:urlShortCode',
        name: 'Store - category - package',
        component: () => import('pages/Store/PackagePreview.vue'),
        props: { baseUrl: '/store' },
        meta: {
          public: true,
          preventRedirect: true,
        },
      },
    ],
  },
  {
    path: '/',
    component: () => import('layouts/MainLayout.vue'),
    children: [
      {
        path: '',
        name: 'Home',
        component: Home,
        meta: {
          offlineModePage: true,
        },
      },
      {
        path: '/run/:sessionId/overview',
        name: 'Session Overview',
        component: Overview,
        meta: {
          offlineModePage: true,
          requiresClient: true,
          requiresActiveSubscription: true,
          requiresClientLicense: true,
        },
      },
      {
        path: '/run/:sessionId/prepare-group-session',
        name: 'Prepare Group Session',
        component: PrepareGroupSession,
        meta: {
          offlineModePage: true,
          requiresClient: true,
          requiresActiveSubscription: true,
          requiresClientLicense: true,
        },
      },
      {
        path: '/run/:sessionId/done/:sessionActivityId',
        name: 'Session Done - Activity',
        component: Done,
        meta: {
          offlineModePage: true,
          requiresClient: true,
          requiresActiveSubscription: true,
          requiresClientLicense: true,
        },
      },
      {
        path: '/run/:sessionId/done',
        name: 'Session Done',
        component: Done,
        meta: {
          offlineModePage: true,
          requiresClient: true,
          requiresActiveSubscription: true,
          requiresClientLicense: true,
        },
      },
      {
        path: '/clients',
        name: 'Clients',
        component: Clients,
        meta: {
          roles: [
            userRoles.MANAGER,
            userRoles.ADMINISTRATOR,
            userRoles.SUPERVISOR,
            userRoles.SENIOR_THERAPIST,
          ],
          offlineModePage: true,
          privileges: ['pages/clients'],
        },
      },
      {
        path: '/history',
        name: 'History',
        component: () => import('pages/History/History.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          offlineModePage: true,
          privileges: ['pages/history'],
        },
      },
      {
        path: '/history/timeline',
        name: 'History - Timeline',
        component: () => import('pages/History/HistoryTimeline.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          offlineModePage: true,
          privileges: ['pages/history'],
        },
      },
      {
        path: '/history/activities',
        name: 'History - Activities',
        component: () => import('pages/History/SessionActivities.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          offlineModePage: true,
          privileges: ['pages/history'],
        },
      },
      {
        path: '/history/:sessionId/edit',
        name: 'History View - Edit',
        component: () => import('pages/History/ViewSession.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          requiresClientLicense: true,
          offlineModePage: true,
        },
        props: {
          action: 'edit',
        },
      },
      {
        path: '/history/:sessionId/:sessionActivityId',
        name: 'History View - Activity',
        component: () => import('pages/History/ViewSession.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          offlineModePage: true,
        },
      },
      {
        path: '/history/:sessionId',
        name: 'History View',
        component: () => import('pages/History/ViewSession.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          offlineModePage: true,
        },
      },
      {
        path: '/notifications-center',
        component: () => import('pages/NotificationsCenter.vue'),
        name: 'Notifications Center',
      },
      {
        path: 'programs',
        name: 'Programs',
        component: Programs,
        meta: {
          offlineModePage: true,
          requiresClient: true,
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/programs'],
        },
      },
      {
        path: 'programs/edit/:programId',
        component: () => import('pages/EditProgram.vue'),
        name: 'Edit Program',
        props: {
          baseUrl: '/programs',
          showPostSaveDialog: true,
          useFolder: true,
        },
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
        },
      },
      {
        path: 'programs/add/:programType',
        component: () => import('pages/EditProgram.vue'),
        name: 'Add Program',
        props: {
          baseUrl: '/programs',
          showPostSaveDialog: true,
          useFolder: true,
        },
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
        },
      },
      {
        path: 'programs/add-from-template/:programId',
        component: () => import('pages/EditProgram.vue'),
        name: 'Add Program from template',
        props: {
          baseUrl: '/programs',
          showPostSaveDialog: true,
          useProgramAsTemplate: true,
          useFolder: true,
        },
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
        },
      },
      {
        path: '/programs/catalog',
        name: 'Programs - Catalog',
        component: () => import('pages/Store/Store.vue'),
        props: {
          baseUrl: '/programs/catalog',
          showBreadcrumb: true,
          hideBreadcrumbClient: false,
        },
        meta: {
          preventRedirect: true,
        },
      },
      {
        path: '/programs/catalog/:categoryTag',
        name: 'Programs - Catalog - category',
        component: () => import('pages/Store/Store.vue'),
        props: {
          baseUrl: '/programs/catalog',
          showBreadcrumb: true,
          hideBreadcrumbClient: false,
        },
        meta: {
          preventRedirect: true,
        },
      },
      {
        path: '/programs/catalog/:categoryTag/:urlShortCode',
        name: 'Programs - Catalog - category - package',
        component: () => import('pages/Store/PackagePreview.vue'),
        props: {
          baseUrl: '/programs/catalog',
          showBreadcrumb: true,
          hideBreadcrumbClient: false,
        },
        meta: {
          preventRedirect: true,
        },
      },
      {
        path: '/appointments',
        name: 'Client Appointments',
        component: () => import('pages/AppointmentsSchedule.vue'),
        meta: {
          offlineModePage: true,
          requiresClient: true,
          requiresActiveSubscription: true,
          privileges: ['pages/clientAppointments'],
        },
      },
      {
        path: '/performance/care-model',
        name: 'Performance - Care Model',
        component: () => import('src/domains/intelligence').then(({ CareModelReport }) => CareModelReport),
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
        },
      },
      {
        path: '/performance/care-model/:cohortId',
        name: 'Performance - Care Model Details',
        component: () => import('src/domains/intelligence').then(
          ({ CareModelDetailsReport }) => CareModelDetailsReport,
        ),
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
        },
      },
      {
        path: '/performance/operations',
        name: 'Performance - Operations',
        component: SimpleLayout,
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER],
        },
      },
      {
        path: '/reporting/record-health',
        name: 'Reporting - Record Health',
        component: () => import('src/contexts/careModel').then((m) => m.RecordHealthPage),
        meta: {
          admin: true,
        },
      },
      {
        path: '/reporting/record-health/update-client-data',
        name: 'Reporting - Update Client Data',
        component: () => import('src/contexts/careModel').then((m) => m.UpdateClientDataPage),
        meta: {
          admin: true,
        },
      },
      {
        path: '/reporting/activities',
        name: 'Reporting - Activities',
        component: () => import('pages/Reporting/Reports/SessionActivityReports.vue'),
        meta: {
          requiresActiveSubscription: true,
          roles: [
            userRoles.MANAGER,
            userRoles.ADMINISTRATOR,
            userRoles.SUPERVISOR,
            userRoles.SENIOR_THERAPIST,
          ],
          privileges: ['pages/reporting'],
        },
      },
      {
        path: '/reporting/appointments',
        name: 'Reporting - Appointments',
        component: () => import('pages/Reporting/Reports/AppointmentReports.vue'),
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR],
          privileges: ['pages/scheduling', 'pages/reporting'],
        },
      },
      {
        path: '/reporting/auditing',
        name: 'Reporting - Auditing',
        component: () => import('pages/Reporting/Reports/AuditLogReports.vue'),
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
        },
      },
      {
        path: '/reporting/outcomes',
        name: 'Reporting - Outcomes',
        redirect: '/',
        component: () => import('pages/Reporting/Reports/ClinicOutcomesReports.vue'),
        meta: {
          roles: [userRoles.MANAGER],
        },
      },
      {
        path: '/scheduling',
        name: 'Scheduling',
        component: () => import('pages/Scheduling/Scheduling.vue'),
        meta: {
          privileges: ['pages/scheduling'],
        },
      },
      {
        path: '/files',
        name: 'files',
        component: () => import('pages/Files'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          privileges: ['pages/files'],
        },
      },
      {
        // The optional :programId keeps deep links to a program working (backwards
        // compatibility) but renders the Progress page and opens the program graph in a
        path: '/progress/:programId?/:section(target)?/:targetId?',
        name: 'Progress',
        component: () => import('pages/Progress/Progress.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          privileges: ['pages/progress'],
          // Reuse the page instance and keep scroll position when only the route params
          // change (e.g. the :programId that drives the program modal), instead of
          // remounting/resetting. Set this on any route that toggles URL-driven content.
          preserveViewOnParamChange: true,
        },
      },
      {
        path: '/progress/insights',
        name: 'Insights',
        component: () => import('pages/Progress/InsightsOverview.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          privileges: ['pages/progress'],
        },
      },
      {
        path: '/progress/reports',
        name: 'Progress Reports',
        component: () => import('src/pages/Progress/Reports.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          privileges: ['pages/progress'],
        },
      },
      {
        path: '/progress/reports/:progressReportId',
        name: 'Progress Report',
        component: () => import('src/pages/Progress/ProgressReport.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
        },
      },
      {
        path: 'progress/:programId/edit',
        component: () => import('pages/EditProgram.vue'),
        name: 'Edit Program (Progress)',
        props: (route) => {
          const from = Array.isArray(route.query.from) ? route.query.from[0] : route.query.from;
          const safeBaseUrl = (typeof from === 'string' && from.startsWith('/progress'))
            ? from
            : '/progress';

          return {
            baseUrl: safeBaseUrl,
            showPostSaveDialog: true,
          };
        },
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
        },
      },
      {
        path: 'progress/insights/:chartFavoriteId',
        name: 'Insights Chart',
        component: () => import('pages/Progress/Insights.vue'),
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
        },
      },
      {
        path: '/progress/assessments',
        name: 'Progress Assessments',
        component: () => import('pages/Assessments/AssessmentsIndex.vue'),
        meta: {
          reuiresClient: true,
          requiresActiveSubscription: true,
          privileges: ['pages/progress'],
        },
      },
      {
        path: '/progress/assessments/:assessmentTypeId',
        name: 'Progress Assessments type',
        component: () => import('pages/Assessments/AssessmentType.vue'),
        meta: {
          reuiresClient: true,
          requiresActiveSubscription: true,
          privileges: ['pages/progress'],
        },
      },
      {
        path: '/activities',
        name: 'Activities',
        component: Activities,
        meta: {
          offlineModePage: true,
        },
      },
      {
        path: '/pending-signatures',
        name: 'Pending Signatures',
        component: PendingSignatures,
        meta: {
          offlineModePage: false,
          privileges: ['pages/pendingSignatures'],
        },
      },
      {
        path: '/my-schedule',
        name: 'My Schedule',
        component: () => import('pages/AppointmentsSchedule.vue'),
        meta: {
          offlineModePage: true,
          privileges: ['pages/mySchedule'],
        },
      },
      {
        path: '/library',
        name: 'Library',
        component: () => import('pages/Library.vue'),
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/library'],
        },
      },
      {
        path: 'library/retrieve/:regionId/:programFolderShareId',
        name: 'Retrieve Library Folder',
        component: () => import('pages/LibraryRetrieve.vue'),
        meta: {
          requiresActiveSubscription: true,
        },
      },
      {
        path: 'library/edit/:programId',
        name: 'Edit Library Program',
        component: () => import('pages/EditProgram.vue'),
        props: {
          baseUrl: '/library',
          showStatus: true,
          showTargetStatus: false,
          useFolder: true,
          showPostSaveDialog: false,
        },
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
        },
      },
      {
        path: 'library/add/:programType',
        name: 'Add Program To Library',
        component: () => import('pages/EditProgram.vue'),
        props: {
          baseUrl: '/library',
          showStatus: true,
          showTargetStatus: false,
          autoAssignClient: false,
          useFolder: true,
          showPostSaveDialog: false,
        },
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
        },
      },
      {
        path: 'library/add-from-template/:programId',
        component: () => import('pages/EditProgram.vue'),
        name: 'Add Program To Library from template',
        props: {
          baseUrl: '/library',
          showStatus: true,
          showTargetStatus: false,
          autoAssignClient: false,
          useFolder: true,
          showPostSaveDialog: false,
          useProgramAsTemplate: true,
        },
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          requiresActiveSubscription: true,
        },
      },
      {
        path: '/library/catalog',
        name: 'Library - Catalog',
        component: () => import('pages/Store/Store.vue'),
        props: {
          baseUrl: '/library/catalog',
          showBreadcrumb: true,
          hideBreadcrumbClient: true,
        },
        meta: {
          preventRedirect: true,
        },
      },
      {
        path: '/library/catalog/:categoryTag',
        name: 'Library - Catalog - category',
        component: () => import('pages/Store/Store.vue'),
        props: {
          baseUrl: '/library/catalog',
          showBreadcrumb: true,
          hideBreadcrumbClient: true,
        },
        meta: {
          preventRedirect: true,
        },
      },
      {
        path: '/library/catalog/:categoryTag/:urlShortCode',
        name: 'Library - Catalog - category - package',
        component: () => import('pages/Store/PackagePreview.vue'),
        props: {
          baseUrl: '/library/catalog',
          showBreadcrumb: true,
          hideBreadcrumbClient: true,
        },
        meta: {
          preventRedirect: true,
        },
      },
      {
        path: '/subscription-expired',
        component: () => import('pages/SubscriptionExpired.vue'),
        name: 'Subscription Expired',
      },
      {
        path: '/no-client-license',
        component: () => import('pages/NoClientLicense.vue'),
        name: 'No Client License',
      },
      {
        path: '/settings',
        component: () => import('pages/Settings.vue'),
        name: 'Settings',
      },
      {
        path: '/settings/subscription',
        component: () => import('pages/Settings/Subscription.vue'),
        name: 'Settings - Subscription',
        meta: {
          roles: [userRoles.MANAGER],
        },
      },
      {
        path: '/settings/prompt-levels',
        component: () => import('pages/Settings/PromptLevels.vue'),
        name: 'Settings - Prompt Levels',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/promptLevels'],
        },
      },
      {
        path: '/settings/program-data',
        component: () => import('src/pages/Settings/ProgramData.vue'),
        name: 'Settings - Program Data',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/programData'],
        },
      },
      {
        path: '/settings/program-templates',
        component: () => import('src/pages/Settings/ProgramTemplates.vue'),
        name: 'Settings - Program Templates',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/programTemplates'],
        },
      },
      {
        path: '/settings/program-templates/edit/:programId',
        name: 'Edit Program Template',
        component: () => import('pages/EditProgram.vue'),
        props: {
          baseUrl: '/settings/program-templates',
          showStatus: true,
          showTargetStatus: false,
          useFolder: true,
          showPostSaveDialog: false,
          isTemplate: true,
        },
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/programTemplates'],
        },
      },
      {
        path: '/settings/program-templates/add/:programType',
        name: 'Add Program To Templates',
        component: () => import('pages/EditProgram.vue'),
        props: {
          baseUrl: '/settings/program-templates',
          showStatus: true,
          showTargetStatus: false,
          autoAssignClient: false,
          useFolder: true,
          showPostSaveDialog: false,
          isTemplate: true,
        },
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/programTemplates'],
        },
      },
      {
        path: '/settings/workflows',
        component: () => import('pages/Settings/Workflows.vue'),
        name: 'Settings - Workflows',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/workflows'],
        },
      },
      // TEMP: Hide program logic settings on production until feature is fully released
      ...(process.env.ENV_TYPE !== 'production' ? [{
        path: '/settings/program-logic',
        component: () => import('pages/Settings/ProgramLogic.vue'),
        name: 'Settings - Program Logic',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
          privileges: ['pages/programLogic'],
        },
      }] : []),
      {
        path: '/settings/session-prototypes',
        component: () => import('pages/Settings/SessionPrototypes.vue'),
        name: 'Settings - Session Prototypes',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/prototype'],
        },
      },
      {
        path: '/settings/supervision',
        component: () => import('pages/Settings/Supervision.vue'),
        name: 'Settings - Supervision',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/supervisionMode'],
        },
      },
      {
        path: '/settings/learnings-moderation',
        component: () => import('src/domains/noteAuditor').then(({ LearningsModeration }) => LearningsModeration),
        name: 'Settings - Learnings Moderation',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR],
          privileges: [AUDITOR_PRIVILEGES.MANAGE_BEHAVIOR],
          adminBypass: true,
        },
      },
      {
        path: '/settings/tags',
        component: () => import('pages/Settings/Tags.vue'),
        name: 'Settings - Tags',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/tags'],
        },
      },
      {
        path: '/settings/treatment-areas',
        component: () => import('pages/Settings/TreatmentAreas.vue'),
        name: 'Settings - Treatment Areas',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/treatmentAreas'],
        },
      },
      {
        path: '/settings/note-templates',
        component: NoteTemplates,
        name: 'Settings - Note Templates',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          offlineModePage: true,
          privileges: ['pages/notes'],
        },
      },
      {
        path: '/settings/form-templates',
        component: NoteTemplates,
        name: 'Settings - Form Templates',
        props: {
          isFormTemplates: true,
        },
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          offlineModePage: true,
          privileges: ['pages/forms'],
        },
      },
      {
        path: '/settings/report-templates',
        component: () => import('pages/Settings/ReportTemplates.vue'),
        name: 'Settings - Report Templates',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/reports'],
        },
      },
      {
        path: '/settings/report-templates/:reportTemplateId',
        component: () => import('pages/Settings/EditReportTemplate.vue'),
        name: 'Settings - Edit Report Template',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/reports'],
        },
      },
      {
        path: '/settings/report-templates/:reportTemplateId/preview/:progressReportId',
        component: () => import('src/pages/Progress/ProgressReport.vue'),
        name: 'Settings - Preview Report Template',
        props: {
          preview: true,
          disableEdit: true,
        },
        meta: {
          requiresClient: true,
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/reports'],
        },
      },
      {
        path: '/settings/report-template-sections',
        component: () => import('src/pages/Settings/ReportTemplateSections.vue'),
        name: 'Settings - Form Section Templates',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/forms'],
        },
      },
      {
        path: '/settings/funding-sources',
        component: () => import('pages/Settings/FundingSources.vue'),
        name: 'Settings - Funding Sources',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/fundingSources'],
        },
      },
      {
        // Not a settings page: it is the clinic's working list for a period, so it sits at the root
        // under Billing rather than under Settings, and is gated on role plus the add-on, with no
        // `pages/*` privilege, which belongs to settings screens.
        //
        // `requiresRevenueCycle` IS NOT `requiresActiveSubscription`. A clinic can be fully paid up
        // and still not have bought billing, and the nav item hiding itself is not a gate: this
        // route is one typed URL away otherwise.
        path: '/billing/billing-items',
        component: () => import('src/contexts/revenueCycle').then((m) => m.BillingItemsPage),
        name: 'Billing - Billing Items',
        meta: {
          requiresActiveSubscription: true,
          requiresRevenueCycle: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
        },
      },
      {
        // The page is published by the Revenue Cycle context rather than living in `pages/Settings`,
        // so it is resolved through that context's `index.js` — the same boundary every other
        // importer of the context goes through. Nothing reaches past it into `ui/pages`.
        path: '/settings/billing/charge-master',
        component: () => import('src/contexts/revenueCycle').then((m) => m.ChargeMasterPage),
        name: 'Settings - Charge Master',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
          privileges: ['pages/fundingSources'],
        },
      },
      {
        path: '/settings/treatment-services',
        component: () => import('pages/Settings/TreatmentServices.vue'),
        name: 'Settings - Treatment Services',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
          privileges: ['pages/treatmentServices'],
        },
      },
      {
        path: '/settings/care-model/cohorts',
        component: () => import('src/domains/careModel').then(({ CohortsSettingsPage }) => CohortsSettingsPage),
        name: 'Settings - Cohorts',
        meta: {
          requiresActiveSubscription: true,
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
        },
      },
      {
        path: '/settings/places-of-service',
        component: () => import('src/pages/Settings/PlacesOfService.vue'),
        name: 'Settings - Places of Service',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
          privileges: ['pages/placeOfService'],
        },
      },
      {
        path: '/settings/cancellation-reasons',
        component: () => import('src/pages/Settings/CancellationReasons.vue'),
        name: 'Settings - Cancellation Reasons',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/cancellationReasons'],
        },
      },
      {
        path: '/settings/global-availability',
        component: () => import('src/pages/Settings/GlobalAvailability.vue'),
        name: 'Settings - Global Availability',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR],
          privileges: ['pages/globalAvailability'],
        },
      },
      {
        path: '/settings/authentication',
        component: () => import('pages/Settings/Authentication.vue'),
        name: 'Settings - Authentication',
        meta: {
          roles: [userRoles.MANAGER],
        },
      },
      {
        path: '/settings/users',
        component: () => import('pages/Settings/Users.vue'),
        name: 'Settings - Users',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR],
          privileges: ['users/manageUsers'],
        },
      },
      {
        path: '/settings/user-privileges',
        component: () => import('pages/Settings/UserPrivileges.vue'),
        name: 'Settings - User Privileges',
        meta: {
          roles: [userRoles.MANAGER],
        },
      },
      {
        path: '/settings/units',
        component: () => import('pages/Settings/UnitsPage.vue'),
        name: 'Settings - Units',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
        },
      },
      {
        path: '/settings/client-information',
        component: () => import('pages/Settings/ClientData.vue'),
        name: 'Settings - Client Information',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/clientData'],
        },
      },
      {
        path: '/settings/staff-member-data',
        component: () => import('pages/Settings/StaffMemberData.vue'),
        name: 'Settings - Staff Information',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/staffInformation'],
        },
      },
      {
        path: '/settings/activity-types',
        component: () => import('pages/Settings/ActivityTypes.vue'),
        name: 'Settings - Activity Types',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR],
        },
      },
      {
        path: '/settings/integrations',
        component: () => import('pages/Settings/Integrations.vue'),
        name: 'Settings - Integrations',
        meta: {
          roles: [userRoles.MANAGER, userRoles.ADMINISTRATOR, userRoles.SUPERVISOR, userRoles.SENIOR_THERAPIST],
          privileges: ['pages/integrations'],
        },
      },
      {
        path: '/settings/offline-access',
        component: OfflineAccess,
        name: 'Settings - Offline Access',
        meta: {
          offlineModePage: true,
        },
      },
      {
        path: '/help',
        redirect: '/help/support',
      },
      {
        path: '/help/app-information',
        component: () => import('src/pages/Help/AppInformation.vue'),
        name: 'App Information',
      },

      {
        path: '/help/support',
        component: () => import('src/pages/Help/ClientSupport.vue'),
        name: 'Client - Support',
      },

      {
        path: '/admin/clinics',
        component: () => import('pages/Admin/AdminClinics.vue'),
        name: 'Admin - Clinics',
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/terms',
        component: () => import('pages/Admin/AdminTerms.vue'),
        name: 'Admin - Terms',
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/support',
        component: () => import('pages/Admin/AdminSupport.vue'),
        name: 'Admin - Support',
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin-users',
        component: () => import('pages/Admin/AdminUsers.vue'),
        name: 'Admin - AdminUsers',
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/user-lookup',
        component: () => import('pages/Admin/AdminUserLookup.vue'),
        name: 'Admin - User Lookup',
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/rbt',
        component: () => import('pages/Admin/AdminRBT.vue'),
        name: 'Admin - RBT',
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/mail-templates',
        component: () => import('pages/Admin/AdminMailTemplates.vue'),
        name: 'Admin - Mail Templates',
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/report-templates',
        component: () => import('pages/Admin/AdminReportTemplates.vue'),
        name: 'Admin - Report Templates',
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/store',
        component: () => import('pages/Admin/AdminStore.vue'),
        name: 'Admin - Store',
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/store/edit/:packageId/info',
        component: () => import('pages/Admin/AdminStoreEditPackage.vue'),
        name: 'Admin - Store - Edit Package',
        props: { baseUrl: '/admin/store' },
        meta: {
          admin: true,
          tab: 'info',
        },
      },
      {
        path: '/admin/store/edit/:packageId/programs',
        component: () => import('pages/Admin/AdminStoreEditPackage.vue'),
        name: 'Admin - Store - Edit Package - Programs',
        props: {
          baseUrl: '/admin/store',
          tab: 'contents',
        },
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/store/edit/:packageId/programs/edit-program/:programId',
        component: () => import('pages/EditProgram.vue'),
        name: 'Admin - Store - Edit Program',
        props: (route) => ({
          baseUrl: `/admin/store/edit/${route.params.packageId}/programs`,
          autoAssignClient: false,
          autoAssignClinic: false,
          showPostSaveDialog: false,
        }),
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/store/edit/:packageId/programs/add-program/:programType',
        component: () => import('pages/EditProgram.vue'),
        name: 'Admin - Store - Add Program',
        props: (route) => ({
          baseUrl: `/admin/store/edit/${route.params.packageId}/programs`,
          autoAssignClient: false,
          autoAssignClinic: false,
          showPostSaveDialog: false,
        }),
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/store/add',
        component: () => import('pages/Admin/AdminStoreEditPackage.vue'),
        name: 'Admin - Store - Add Package',
        props: { baseUrl: '/admin/store' },
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/outcomes/clinics',
        component: () => import('pages/Admin/AdminOutcomesClinicList.vue'),
        name: 'Admin - Outcomes - Clinics',
        props: { baseUrl: '/admin/outcomes/clinics' },
        meta: {
          admin: true,
        },
      },
      {
        path: '/admin/outcomes/clinics/:region/:clinicId',
        component: () => import('pages/Admin/AdminClinicOutcomesReports.vue'),
        name: 'Admin - Outcomes - Clinic Reports',
        props: { baseUrl: '/admin/outcomes/clinics' },
        meta: {
          admin: true,
        },
      },
    ],
  },
];

// Always leave this as last one
routes.push({
  path: '/:catchAll(.*)*',
  component: SimpleLayout,
  children: [
    { path: '', component: () => import('pages/Error404.vue') },
  ],
});

export default routes;
